// The 12-step patient journey (proposal Section 06): welcome → identify → consent → open visit →
// readiness → measure → validate → review → route → print → synchronise → reset.
// Every step has an entry condition, a recorded outcome and a recovery path.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  assessReading, computeBmi, describeVitals, extractIntake, normaliseUnit, triage, VITALS,
  type IntakeResult, type ThresholdSet, type TriageResult, type VitalCode, type VitalValues,
} from '@shared';
import { ConsultCall, type CallMode } from '../lib/rtc.ts';
import type { ServerMessage } from '../lib/realtime.ts';
import { DEVICE_ORDER, printReceipt, type DeviceKind, type Reading, type SelfTest } from './devices.ts';
import type { KioskServices } from './KioskApp.tsx';
import { createRecognizer, speak, stopSpeaking } from './speech.ts';
import { STRINGS, SYMPTOM_CHIPS, type Lang, type Strings } from './strings.ts';
import { FacilityStrip, KioskFooter, KioskHeader } from './frame.tsx';

type Step = 'welcome' | 'identify' | 'consent' | 'declined' | 'readiness' | 'measure' | 'symptoms' | 'review' | 'route' | 'print' | 'consult' | 'done';

export interface RxLine { drugId: string; name?: string; dose: string; frequency: string; days: number; instructions?: string }

interface PatientState {
  patientId: string;
  matched: boolean;
  maskedName?: string | null;
  cnicMasked?: string | null;
}

interface Session {
  sessionId: string;
  encounterId: string;
  lang: Lang;
  assisted: boolean;
  patient: PatientState | null;
  sensors: Partial<Record<DeviceKind, SelfTest>>;
  vitals: VitalValues;
  escalated: VitalCode[];
  intake: IntakeResult | null;
  triage: TriageResult | null;
  consultId: string | null;
  consultMode: CallMode;
  visitCode: string;
  next: 'consult' | 'done';
}

const VISIT_ALPHABET = 'ACDEFGHJKLMNPQRTUVWXY3479';
const newVisitCode = () => {
  const b = crypto.getRandomValues(new Uint8Array(6));
  const s = [...b].map((x) => VISIT_ALPHABET[x % VISIT_ALPHABET.length]).join('');
  return `${s.slice(0, 3)}-${s.slice(3)}`;
};

const freshSession = (lang: Lang = 'ur'): Session => ({
  sessionId: crypto.randomUUID(),
  encounterId: crypto.randomUUID(),
  lang,
  assisted: false,
  patient: null,
  sensors: {},
  vitals: {},
  escalated: [],
  intake: null,
  triage: null,
  consultId: null,
  consultMode: 'video',
  visitCode: newVisitCode(),
  next: 'done',
});

const PROGRESS: Partial<Record<Step, number>> = { identify: 0, consent: 1, readiness: 2, measure: 3, symptoms: 4, review: 5, route: 6, print: 6, consult: 6 };

export function Journey({ svc }: { svc: KioskServices }) {
  const [step, setStep] = useState<Step>('welcome');
  const [s, setS] = useState<Session>(() => freshSession());
  const [idlePrompt, setIdlePrompt] = useState(false);
  const lastTouch = useRef(Date.now());
  const t = STRINGS[s.lang];
  const update = useCallback((patch: Partial<Session>) => setS((prev) => ({ ...prev, ...patch })), []);
  const go = useCallback((next: Step) => {
    stopSpeaking();
    setStep(next);
  }, []);

  const reset = useCallback(() => {
    stopSpeaking();
    svc.sim.reset();
    setIdlePrompt(false);
    setS(freshSession());
    setStep('welcome');
  }, [svc.sim]);

  const abandon = useCallback(async () => {
    if (step !== 'welcome') await svc.send('session.abandon', { sessionId: s.sessionId, lastStep: step, at: new Date().toISOString() });
    reset();
  }, [reset, s.sessionId, step, svc]);

  // Inactivity: prompt after the configured idle time, then clear the screen (privacy reset).
  useEffect(() => {
    const touch = () => {
      lastTouch.current = Date.now();
    };
    window.addEventListener('pointerdown', touch);
    window.addEventListener('keydown', touch);
    const idleMs = (svc.config.config.idleResetSeconds ?? 90) * 1000;
    const iv = setInterval(() => {
      if (step === 'welcome' || step === 'consult' || step === 'done') return;
      const idle = Date.now() - lastTouch.current;
      if (idle > idleMs + 20000) void abandon();
      else if (idle > idleMs) setIdlePrompt(true);
    }, 1000);
    return () => {
      window.removeEventListener('pointerdown', touch);
      window.removeEventListener('keydown', touch);
      clearInterval(iv);
    };
  }, [abandon, step, svc.config.config.idleResetSeconds]);

  const progress = PROGRESS[step];
  return (
    <div className="kiosk" dir={s.lang === 'ur' ? 'rtl' : 'ltr'} lang={s.lang}>
      <KioskHeader lang={s.lang} onSealDown={svc.onBrandPress} onSealUp={svc.onBrandRelease}>
        {!svc.online && <span className="badge badge-warn">● {t.offlineBadge}</span>}
        {svc.queueDepth > 0 && <span className="badge">{svc.queueDepth} {t.queued}</span>}
        {s.assisted && <span className="badge badge-info">{t.staffAssistOn}</span>}
      </KioskHeader>
      <FacilityStrip lang={s.lang} facility={svc.config.facility} kioskId={svc.config.kioskId} />
      {progress !== undefined && (
        <ol className="k-progress" aria-label="progress">
          {t.progress.map((label, i) => (
            <li key={label} className={i < progress ? 'done' : i === progress ? 'current' : ''}>
              <span className="dot">{i < progress ? '✓' : i + 1}</span>
              <span className="label">{label}</span>
            </li>
          ))}
        </ol>
      )}
      <main className="k-main">
        {step === 'welcome' && (
          <Welcome
            assisted={s.assisted}
            onAssisted={(v) => update({ assisted: v })}
            onStart={async (lang) => {
              const next = { ...freshSession(lang), assisted: s.assisted };
              setS(next);
              lastTouch.current = Date.now();
              await svc.send('session.open', { sessionId: next.sessionId, encounterId: next.encounterId, language: lang, mode: next.assisted ? 'assisted' : 'self', startedAt: new Date().toISOString() });
              go('identify');
            }}
          />
        )}
        {step === 'identify' && <Identify t={t} s={s} svc={svc} onDone={(patient) => { update({ patient }); go('consent'); }} onCancel={abandon} />}
        {step === 'consent' && (
          <Consent
            t={t}
            s={s}
            onAgree={async (whatsapp, research) => {
              await svc.send('consent.record', { sessionId: s.sessionId, consentId: crypto.randomUUID(), version: svc.config.consentVersion, care: true, whatsapp, research });
              go('readiness');
            }}
            onDecline={() => go('declined')}
          />
        )}
        {step === 'declined' && (
          <Card title={t.declinedTitle} body={t.declinedBody}>
            <button className="btn btn-primary btn-xl" onClick={abandon}>{t.endSession}</button>
          </Card>
        )}
        {step === 'readiness' && <Readiness t={t} s={s} svc={svc} onReady={(sensors) => { update({ sensors }); go('measure'); }} />}
        {step === 'measure' && (
          <Measure t={t} s={s} svc={svc} onDone={(vitals, escalated) => { update({ vitals, escalated }); go('symptoms'); }} onCancel={abandon} />
        )}
        {step === 'symptoms' && (
          <Symptoms
            t={t}
            s={s}
            onDone={async (text) => {
              const intake = text.trim() ? extractIntake(text) : null;
              update({ intake });
              if (intake) await svc.send('intake.record', { sessionId: s.sessionId, text });
              go('review');
            }}
          />
        )}
        {step === 'review' && <Review t={t} s={s} svc={svc} onDone={(tri) => { update({ triage: tri }); go('route'); }} />}
        {step === 'route' && (
          <RouteStep
            t={t}
            s={s}
            svc={svc}
            onChoose={async (choice) => {
              if (choice === 'video' || choice === 'audio') {
                const consultId = s.consultId ?? crypto.randomUUID();
                if (!s.consultId) await svc.send('consult.request', { sessionId: s.sessionId, consultId, mode: svc.online ? choice : 'async' });
                update({ consultId, consultMode: choice, next: 'consult' });
              } else update({ next: 'done' });
              go('print');
            }}
            onUrgentConsult={async () => {
              if (s.consultId) return;
              const consultId = crypto.randomUUID();
              await svc.send('consult.request', { sessionId: s.sessionId, consultId, mode: svc.online ? 'video' : 'async' });
              update({ consultId });
            }}
          />
        )}
        {step === 'print' && <PrintStep t={t} s={s} svc={svc} onDone={() => go(s.next)} />}
        {step === 'consult' && <ConsultStep t={t} s={s} svc={svc} onDone={() => go('done')} />}
        {step === 'done' && <Done t={t} s={s} svc={svc} onReset={reset} />}
      </main>
      <KioskFooter lang={s.lang} />
      {idlePrompt && (
        <div className="k-modal" role="dialog">
          <div className="k-modal-card">
            <img src="/brand/seal.svg" alt="" className="k-modal-seal" />
            <h2>{t.stillThere}</h2>
            <div className="row">
              <button className="btn btn-primary btn-xl" onClick={() => { lastTouch.current = Date.now(); setIdlePrompt(false); }}>{t.continue}</button>
              <button className="btn btn-xl" onClick={abandon}>{t.endSession}</button>
            </div>
          </div>
        </div>
      )}
      <Receipt t={t} s={s} svc={svc} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

function Card({ title, body, children, icon }: { title: string; body?: string; children?: ReactNode; icon?: string }) {
  return (
    <section className="k-card">
      {icon && <div className="k-icon" aria-hidden>{icon}</div>}
      <h1>{title}</h1>
      {body && <p className="lead">{body}</p>}
      {children}
    </section>
  );
}

function useSpeak(text: string, lang: Lang) {
  useEffect(() => {
    const id = setTimeout(() => speak(text, lang), 300);
    return () => clearTimeout(id);
  }, [text, lang]);
}

function Welcome({ assisted, onAssisted, onStart }: { assisted: boolean; onAssisted: (v: boolean) => void; onStart: (l: Lang) => void }) {
  return (
    <section className="k-card k-welcome">
      <div className="k-hero-ur" lang="ur" dir="rtl">{STRINGS.ur.welcomeTitle}</div>
      <div className="k-hero-en" lang="en" dir="ltr">{STRINGS.en.welcomeTitle}</div>
      <p className="lead" lang="ur" dir="rtl">{STRINGS.ur.welcomeBody}</p>
      <p className="lead en-sub" lang="en" dir="ltr">{STRINGS.en.welcomeBody}</p>
      <div className="k-lang">
        <button className="btn btn-primary btn-xxl" lang="ur" onClick={() => onStart('ur')}>اردو میں شروع کریں</button>
        <button className="btn btn-secondary btn-xxl" lang="en" dir="ltr" onClick={() => onStart('en')}>Start in English</button>
      </div>
      <label className="k-toggle">
        <input type="checkbox" checked={assisted} onChange={(e) => onAssisted(e.target.checked)} />
        <span><span lang="ur">{STRINGS.ur.staffAssist}</span> · <span lang="en">{STRINGS.en.staffAssist}</span></span>
      </label>
    </section>
  );
}

// --- Identify (E02) ------------------------------------------------------------------------

function formatCnic(d: string) {
  return [d.slice(0, 5), d.slice(5, 12), d.slice(12, 13)].filter(Boolean).join('-');
}

function Keypad({ value, onChange, max }: { value: string; onChange: (v: string) => void; max: number }) {
  return (
    <div className="keypad" dir="ltr">
      {['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', 'C'].map((k) => (
        <button key={k} className="key" onClick={() => {
          if (k === '⌫') onChange(value.slice(0, -1));
          else if (k === 'C') onChange('');
          else if (value.length < max) onChange(value + k);
        }}>{k}</button>
      ))}
    </div>
  );
}

function Identify({ t, s, svc, onDone, onCancel }: { t: Strings; s: Session; svc: KioskServices; onDone: (p: PatientState) => void; onCancel: () => void }) {
  const [mode, setMode] = useState<'choose' | 'cnic' | 'card' | 'qr' | 'confirm' | 'new'>('choose');
  const [cnic, setCnic] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [found, setFound] = useState<{ patientId: string; maskedName: string | null; cnicMasked: string | null } | null>(null);
  const [ident, setIdent] = useState<{ cnic?: string; cardUid?: string }>({});
  const [form, setForm] = useState({ name: '', sex: '' as '' | 'female' | 'male' | 'other', birthYear: '', phone: '' });
  const [qr, setQr] = useState('');
  useSpeak(t.identifyBody, s.lang);

  const lookup = async (method: 'cnic' | 'card' | 'visit', value: string) => {
    setBusy(true);
    setError('');
    try {
      if (!svc.online) throw new Error('offline');
      const r = await svc.api.post<{ found: boolean; patientId?: string; maskedName?: string | null; cnicMasked?: string | null }>('/api/kiosk/identify', { method, value });
      if (r.found) {
        setFound({ patientId: r.patientId!, maskedName: r.maskedName ?? null, cnicMasked: r.cnicMasked ?? null });
        setMode('confirm');
      } else setMode('new');
    } catch {
      setError(svc.online ? '' : t.offlineIdentify);
      setMode('new');
    } finally {
      setBusy(false);
    }
  };

  const link = async (patientId: string, extra: Record<string, unknown>, matched: boolean, masked?: { maskedName?: string | null; cnicMasked?: string | null }) => {
    const r = await svc.send('patient.link', { sessionId: s.sessionId, patientId, ...ident, ...extra });
    const res = r?.result as { patientId?: string; matched?: boolean } | undefined;
    onDone({ patientId: res?.patientId ?? patientId, matched: res?.matched ?? matched, ...masked });
  };

  if (mode === 'choose') {
    return (
      <Card title={t.identifyTitle} body={t.identifyBody}>
        <div className="grid-2">
          <button className="tile" onClick={() => setMode('cnic')}><span className="tile-icon">🪪</span>{t.byCnic}</button>
          <button className="tile" onClick={() => setMode('card')}><span className="tile-icon">📶</span>{t.byCard}</button>
          <button className="tile" onClick={() => setMode('qr')}><span className="tile-icon">▦</span>{t.byQr}</button>
          <button className="tile tile-quiet" onClick={() => { setIdent({}); setMode('new'); }}><span className="tile-icon">👤</span>{t.asGuest}</button>
        </div>
        <div className="k-footer"><button className="btn" onClick={onCancel}>{t.cancel}</button></div>
      </Card>
    );
  }
  if (mode === 'cnic') {
    return (
      <Card title={t.byCnic} body={t.enterCnic}>
        <div className="cnic-display" dir="ltr">{formatCnic(cnic) || '_____-_______-_'}</div>
        {error && <p className="error">{error}</p>}
        <Keypad value={cnic} onChange={setCnic} max={13} />
        <div className="k-footer">
          <button className="btn" onClick={() => setMode('choose')}>{t.back}</button>
          <button className="btn btn-primary btn-xl" disabled={busy} onClick={() => {
            if (cnic.length !== 13) return setError(t.cnicInvalid);
            setIdent({ cnic });
            void lookup('cnic', cnic);
          }}>{busy ? t.lookingUp : t.next}</button>
        </div>
      </Card>
    );
  }
  if (mode === 'card') {
    return (
      <Card title={t.byCard} body={t.tapCard} icon="📶">
        <div className="nfc-pad pulse" onClick={() => {
          // Simulated tap; on the Model H the NFC reader event arrives through the Android bridge.
          const uid = `04${Array.from(crypto.getRandomValues(new Uint8Array(6)), (b) => b.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
          setIdent({ cardUid: uid });
          void lookup('card', uid);
        }}>{busy ? t.lookingUp : 'NFC'}</div>
        <div className="k-footer"><button className="btn" onClick={() => setMode('choose')}>{t.back}</button></div>
      </Card>
    );
  }
  if (mode === 'qr') {
    return (
      <Card title={t.byQr} body={t.scanQr} icon="▦">
        <input className="input input-xl" dir="ltr" placeholder={`${t.visitCode} (ABC-123)`} value={qr} onChange={(e) => setQr(e.target.value.toUpperCase())} autoFocus />
        <div className="k-footer">
          <button className="btn" onClick={() => setMode('choose')}>{t.back}</button>
          <button className="btn btn-primary btn-xl" disabled={!qr || busy} onClick={() => lookup('visit', qr)}>{busy ? t.lookingUp : t.next}</button>
        </div>
      </Card>
    );
  }
  if (mode === 'confirm' && found) {
    return (
      <Card title={t.isThisYou}>
        <div className="confirm-id">
          <div className="masked-name" dir="ltr">{found.maskedName ?? '—'}</div>
          {found.cnicMasked && <div className="muted" dir="ltr">{found.cnicMasked}</div>}
        </div>
        <div className="row">
          <button className="btn btn-primary btn-xl" onClick={() => link(found.patientId, {}, true, found)}>{t.yes}</button>
          <button className="btn btn-xl" onClick={() => { setFound(null); setIdent({}); setMode('new'); }}>{t.no}</button>
        </div>
      </Card>
    );
  }
  // New patient — minimum necessary data.
  const year = new Date().getFullYear();
  const by = Number(form.birthYear);
  const validYear = !form.birthYear || (by >= year - 110 && by <= year);
  return (
    <Card title={t.newPatient}>
      {error && <p className="notice">{error}</p>}
      <div className="form">
        <label>{t.nameOptional}<input className="input" value={form.name} maxLength={80} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <div className="label">{t.sex}</div>
        <div className="seg">
          {(['female', 'male', 'other'] as const).map((x) => (
            <button key={x} className={`seg-btn ${form.sex === x ? 'on' : ''}`} onClick={() => setForm({ ...form, sex: x })}>{t[x]}</button>
          ))}
        </div>
        <label>{t.birthYear}<input className="input" dir="ltr" inputMode="numeric" value={form.birthYear} maxLength={4} onChange={(e) => setForm({ ...form, birthYear: e.target.value.replace(/\D/g, '') })} /></label>
        <label>{t.phoneOptional}<input className="input" dir="ltr" inputMode="tel" placeholder="03XX XXXXXXX" value={form.phone} maxLength={14} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d+]/g, '') })} /></label>
      </div>
      <div className="k-footer">
        <button className="btn" onClick={() => setMode('choose')}>{t.back}</button>
        <button className="btn btn-primary btn-xl" disabled={!form.sex || !validYear} onClick={() => link(crypto.randomUUID(), {
          name: form.name.trim() || undefined,
          sex: form.sex,
          birthYear: form.birthYear ? by : undefined,
          phone: form.phone ? (form.phone.startsWith('0') ? `+92${form.phone.slice(1)}` : form.phone) : undefined,
          guest: !ident.cnic && !ident.cardUid,
        }, false)}>{t.next}</button>
      </div>
    </Card>
  );
}

// --- Consent (E02) -------------------------------------------------------------------------

function Consent({ t, s, onAgree, onDecline }: { t: Strings; s: Session; onAgree: (whatsapp: boolean, research: boolean) => void; onDecline: () => void }) {
  const [whatsapp, setWhatsapp] = useState(false);
  const [research, setResearch] = useState(false);
  useSpeak(t.consentCare, s.lang);
  return (
    <Card title={t.consentTitle}>
      <div className="consent-required">
        <p className="lead">{t.consentCare}</p>
        <p className="muted">{t.consentCareNote}</p>
      </div>
      <label className="check"><input type="checkbox" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} /> {t.consentWhatsapp}</label>
      <label className="check"><input type="checkbox" checked={research} onChange={(e) => setResearch(e.target.checked)} /> {t.consentResearch}</label>
      <div className="k-footer">
        <button className="btn btn-xl" onClick={onDecline}>{t.decline}</button>
        <button className="btn btn-primary btn-xl" onClick={() => onAgree(whatsapp, research)}>{t.agree}</button>
      </div>
    </Card>
  );
}

// --- Readiness -----------------------------------------------------------------------------

function Readiness({ t, s, svc, onReady }: { t: Strings; s: Session; svc: KioskServices; onReady: (sensors: Partial<Record<DeviceKind, SelfTest>>) => void }) {
  const [sensors, setSensors] = useState<Partial<Record<DeviceKind, SelfTest>>>({});
  const [paper, setPaper] = useState<string>('…');
  useSpeak(t.readinessTitle, s.lang);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results: Partial<Record<DeviceKind, SelfTest>> = {};
      await Promise.all(DEVICE_ORDER.map(async (k) => {
        results[k] = await svc.adapters[k].selfTest();
        if (!cancelled) setSensors({ ...results });
      }));
      const p = await svc.paperState();
      if (cancelled) return;
      setPaper(p);
      svc.reportSensors(results);
      setTimeout(() => !cancelled && onReady(results), 900);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const mark = (v?: string) => (v === undefined || v === '…' ? <span className="spin" /> : v === 'ok' ? <span className="ok">✓</span> : <span className="warn">!</span>);
  const allSensors = DEVICE_ORDER.every((k) => sensors[k]);
  return (
    <Card title={t.readinessTitle}>
      <ul className="checklist">
        <li>{mark(svc.online ? 'ok' : 'offline')} {t.checkNetwork}{!svc.online && <span className="muted"> — {t.networkOffline}</span>}</li>
        <li>{mark(allSensors ? (DEVICE_ORDER.every((k) => sensors[k] === 'ok') ? 'ok' : 'warn') : undefined)} {t.checkSensors}
          <span className="muted small"> {DEVICE_ORDER.map((k) => `${svc.adapters[k].name.split(' (')[0]}: ${sensors[k] ?? '…'}`).join(' · ')}</span></li>
        <li>{mark(paper === '…' ? undefined : paper === 'ok' ? 'ok' : 'warn')} {t.checkPrinter} <span className="muted small">{paper}</span></li>
      </ul>
    </Card>
  );
}

// --- Measure & validate (E03, E04) ---------------------------------------------------------

interface DeviceRun {
  status: 'idle' | 'measuring' | 'accepted' | 'repeat' | 'implausible' | 'escalate' | 'unavailable' | 'skipped';
  attempt: number;
  progress: number;
  readings: Reading[];
}

const DEVICE_TEXT: Record<DeviceKind, { title: keyof Strings; how: keyof Strings; icon: string }> = {
  body: { title: 'bodyTitle', how: 'bodyHow', icon: '🧍' },
  bp: { title: 'bpTitle', how: 'bpHow', icon: '🩺' },
  oximeter: { title: 'oxTitle', how: 'oxHow', icon: '☝️' },
  thermometer: { title: 'tempTitle', how: 'tempHow', icon: '🌡️' },
};

function Measure({ t, s, svc, onDone, onCancel }: { t: Strings; s: Session; svc: KioskServices; onDone: (v: VitalValues, escalated: VitalCode[]) => void; onCancel: () => void }) {
  const [idx, setIdx] = useState(0);
  const [runs, setRuns] = useState<Record<DeviceKind, DeviceRun>>(() =>
    Object.fromEntries(DEVICE_ORDER.map((k) => [k, { status: s.sensors[k] && s.sensors[k] !== 'ok' && s.sensors[k] !== 'calibration_due' ? 'unavailable' : 'idle', attempt: 0, progress: 0, readings: [] }])) as unknown as Record<DeviceKind, DeviceRun>);
  const vitals = useRef<VitalValues>({});
  const escalated = useRef<VitalCode[]>([]);
  const abort = useRef<AbortController | null>(null);
  const kind = DEVICE_ORDER[idx];
  const run = runs[kind];
  const text = DEVICE_TEXT[kind];
  useSpeak(String(t[text.how]), s.lang);
  useEffect(() => () => abort.current?.abort(), []);

  const set = (k: DeviceKind, patch: Partial<DeviceRun>) => setRuns((r) => ({ ...r, [k]: { ...r[k], ...patch } }));

  const advance = useCallback(() => {
    if (idx + 1 < DEVICE_ORDER.length) setIdx(idx + 1);
    else {
      const v = { ...vitals.current };
      const bmi = computeBmi(v.height, v.weight);
      if (bmi !== undefined) v.bmi = bmi;
      onDone(v, escalated.current);
    }
  }, [idx, onDone]);

  const start = async () => {
    const attempt = run.attempt + 1;
    set(kind, { status: 'measuring', progress: 0, attempt });
    abort.current = new AbortController();
    try {
      const readings = await svc.adapters[kind].capture((p) => set(kind, { progress: p }), abort.current.signal);
      let worst: DeviceRun['status'] = 'accepted';
      for (const r of readings) {
        const value = normaliseUnit(r.code, r.value, r.unit);
        const outcome = assessReading(r.code, value, r.quality, attempt);
        await svc.send('observation.record', {
          sessionId: s.sessionId, observationId: crypto.randomUUID(), code: r.code, value: r.value, unit: r.unit,
          quality: Math.round(r.quality * 100) / 100, attempt, device: r.device, effectiveAt: new Date().toISOString(),
        }, { flush: false });
        if (outcome === 'accepted') vitals.current[r.code] = value;
        else if (outcome === 'escalate') {
          worst = 'escalate';
          escalated.current.push(r.code);
        } else if (worst !== 'escalate') worst = outcome === 'rejected_implausible' ? 'implausible' : 'repeat';
      }
      set(kind, { status: worst, readings });
      void svc.flush();
      if (worst === 'accepted') setTimeout(advance, 1600);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') set(kind, { status: 'escalate' });
    }
  };

  return (
    <section className="k-card">
      <div className="device-steps">
        {DEVICE_ORDER.map((k, i) => (
          <span key={k} className={`device-step ${i === idx ? 'current' : ''} ${runs[k].status === 'accepted' ? 'done' : ''}`}>{DEVICE_TEXT[k].icon} {String(t[DEVICE_TEXT[k].title])}</span>
        ))}
      </div>
      <div className="k-icon big" aria-hidden>{text.icon}</div>
      <h1>{String(t[text.title])}</h1>
      <p className="lead">{String(t[text.how])}</p>
      {run.status === 'unavailable' && <p className="notice">{t.deviceUnavailable}</p>}
      {run.status === 'measuring' && (
        <div className="measuring">
          <div className="meter"><div className="meter-fill" style={{ width: `${Math.round(run.progress * 100)}%` }} /></div>
          <p>{t.measuring}</p>
        </div>
      )}
      {(run.status === 'accepted' || run.status === 'repeat' || run.status === 'implausible' || run.status === 'escalate') && run.readings.length > 0 && (
        <div className={`readings ${run.status}`}>
          {run.readings.filter((r) => r.code !== 'bp_dia').map((r) => {
            const dia = r.code === 'bp_sys' ? run.readings.find((x) => x.code === 'bp_dia') : undefined;
            return (
              <div key={r.code} className="reading">
                <div className="reading-label">{r.code === 'bp_sys' ? t.bpTitle : s.lang === 'ur' ? VITALS[r.code].ur : VITALS[r.code].display}</div>
                <div className="reading-value" dir="ltr">{r.value}{dia ? `/${dia.value}` : ''} <small>{VITALS[r.code].unitLabel}</small></div>
              </div>
            );
          })}
        </div>
      )}
      {run.status === 'accepted' && <p className="ok-text">✓ {t.accepted}</p>}
      {run.status === 'repeat' && <p className="notice">{t.repeatReading} ({t.attempt} {run.attempt}/3)</p>}
      {run.status === 'implausible' && <p className="notice">{t.implausible} ({t.attempt} {run.attempt}/3)</p>}
      {run.status === 'escalate' && <p className="error">{t.escalateReading}</p>}
      <div className="k-footer">
        <button className="btn" onClick={() => { abort.current?.abort(); if (run.status !== 'accepted') set(kind, { status: 'skipped' }); advance(); }} disabled={run.status === 'measuring'}>
          {run.status === 'escalate' || run.status === 'unavailable' ? t.next : t.skip}
        </button>
        {(run.status === 'idle' || run.status === 'repeat' || run.status === 'implausible') && (
          <button className="btn btn-primary btn-xl" onClick={start}>{run.status === 'idle' ? t.measureStart : t.tryAgain}</button>
        )}
        {idx === 0 && run.status === 'idle' && <button className="btn btn-ghost" onClick={onCancel}>{t.cancel}</button>}
      </div>
    </section>
  );
}

// --- Symptoms (Urdu NLP intake) ------------------------------------------------------------

function Symptoms({ t, s, onDone }: { t: Strings; s: Session; onDone: (text: string) => void }) {
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const recognizer = useMemo(() => createRecognizer(s.lang), [s.lang]);
  const intake = useMemo(() => (text.trim() ? extractIntake(text) : null), [text]);
  useSpeak(t.symptomsTitle, s.lang);

  const toggleChip = (phrase: string) => {
    const parts = text.split(/[،,]\s*/).map((p) => p.trim()).filter(Boolean);
    const i = parts.indexOf(phrase);
    if (i >= 0) parts.splice(i, 1);
    else parts.push(phrase);
    setText(parts.join(s.lang === 'ur' ? '، ' : ', '));
  };

  const listen = () => {
    if (!recognizer) return;
    recognizer.onresult = (e) => setText((prev) => `${prev}${prev ? ' ' : ''}${e.results[0][0].transcript}`);
    recognizer.onend = () => setListening(false);
    recognizer.onerror = () => setListening(false);
    setListening(true);
    recognizer.start();
  };

  return (
    <Card title={t.symptomsTitle} body={t.symptomsBody}>
      <div className="chips">
        {SYMPTOM_CHIPS.map((c) => {
          const on = intake?.findings.some((f) => f.id === c.id && !f.negated);
          return <button key={c.id} className={`chip ${on ? 'on' : ''}`} onClick={() => toggleChip(c.phrase[s.lang])}>{c[s.lang]}</button>;
        })}
      </div>
      <div className="intake-row">
        <textarea className="input textarea" value={text} placeholder={t.typeHere} onChange={(e) => setText(e.target.value)} rows={3} />
        {recognizer && <button className={`btn mic ${listening ? 'on' : ''}`} onClick={listen}>🎤 {listening ? t.listening : t.speakNow}</button>}
      </div>
      {intake && intake.findings.length > 0 && (
        <div className="understood">
          <div className="muted small">{t.understood}</div>
          {intake.findings.map((f) => (
            <span key={f.id} className={`chip static ${f.negated ? 'neg' : ''} ${f.redFlag && !f.negated ? 'red' : ''}`}>
              {f.negated ? '✕ ' : ''}{s.lang === 'ur' ? f.ur : f.en}
            </span>
          ))}
          {intake.durationDays && <span className="chip static">⏱ {intake.durationDays} {s.lang === 'ur' ? 'دن' : 'days'}</span>}
        </div>
      )}
      <div className="k-footer">
        <button className="btn" onClick={() => onDone('')}>{t.noSymptoms}</button>
        <button className="btn btn-primary btn-xl" onClick={() => onDone(text)} disabled={!text.trim()}>{t.next}</button>
      </div>
    </Card>
  );
}

// --- Review (plain-language, no diagnosis) -------------------------------------------------

function Review({ t, s, svc, onDone }: { t: Strings; s: Session; svc: KioskServices; onDone: (tri: TriageResult) => void }) {
  const thresholds = svc.config.thresholds as ThresholdSet;
  const local = useMemo(() => triage(s.vitals, s.intake?.findings ?? [], thresholds), [s.vitals, s.intake, thresholds]);
  const [tri, setTri] = useState<TriageResult>(local);
  const lines = useMemo(() => describeVitals(s.vitals, thresholds), [s.vitals, thresholds]);
  useSpeak(`${t.reviewTitle}. ${lines.map((l) => (s.lang === 'ur' ? l.ur : l.en)).join('. ')}. ${t.reviewNote}`, s.lang);
  useEffect(() => {
    let cancelled = false;
    // The server re-runs triage on the stored record and is authoritative when reachable.
    svc.send('session.triage', { sessionId: s.sessionId }).then((r) => {
      if (!cancelled && r?.status !== 'rejected' && r?.result) setTri(r.result as TriageResult);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Card title={t.reviewTitle}>
      <ul className="vital-lines">
        {lines.map((l) => (
          <li key={l.code} className={`vital ${l.status}`}>
            <span className="vital-icon" aria-hidden>{l.status === 'normal' ? '✓' : l.status === 'attention' ? '!' : l.status === 'high' ? '↑' : '↓'}</span>
            <span>{s.lang === 'ur' ? l.ur : l.en}</span>
          </li>
        ))}
        {s.escalated.length > 0 && <li className="vital attention"><span className="vital-icon">!</span><span>{t.escalateReading}</span></li>}
        {lines.length === 0 && <li className="vital">—</li>}
      </ul>
      <p className="muted">{t.reviewNote}</p>
      <div className="k-footer">
        <button className="btn btn-primary btn-xl" onClick={() => onDone(tri)}>{t.next}</button>
      </div>
    </Card>
  );
}

// --- Route ---------------------------------------------------------------------------------

function RouteStep({ t, s, svc, onChoose, onUrgentConsult }: { t: Strings; s: Session; svc: KioskServices; onChoose: (c: 'video' | 'audio' | 'queue') => void; onUrgentConsult: () => void }) {
  const tri = s.triage!;
  const urgent = tri.route === 'urgent_review' || s.escalated.length > 0;
  const [cleared, setCleared] = useState(!urgent);
  const [pin, setPin] = useState('');
  const [pinOpen, setPinOpen] = useState(false);
  const staffPin = String(svc.config.config.staffPin ?? '1234');
  useSpeak(urgent ? t.routeUrgentBody : tri.route === 'teleconsult' ? t.routeConsultBody : t.routeQueueBody, s.lang);
  useEffect(() => {
    if (urgent) onUrgentConsult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (urgent && !cleared) {
    // An urgent result cannot be dismissed by the patient — staff confirm they have seen them.
    return (
      <section className="k-card urgent">
        <div className="k-icon big" aria-hidden>⚠️</div>
        <h1>{t.routeUrgentTitle}</h1>
        <p className="lead">{t.routeUrgentBody}</p>
        <ul className="vital-lines">
          {tri.flags.filter((f) => f.severity === 'urgent').map((f) => (
            <li key={f.rule} className="vital attention"><span className="vital-icon">!</span><span>{s.lang === 'ur' ? f.ur : f.en}</span></li>
          ))}
        </ul>
        {!pinOpen ? (
          <button className="btn btn-ghost" onClick={() => setPinOpen(true)}>{t.staffConfirm}</button>
        ) : (
          <div className="pin-box">
            <div className="cnic-display" dir="ltr">{'•'.repeat(pin.length) || '····'}</div>
            <Keypad value={pin} onChange={(v) => { setPin(v); if (v === staffPin) setCleared(true); }} max={6} />
          </div>
        )}
      </section>
    );
  }
  const title = urgent ? t.routeConsultTitle : tri.route === 'teleconsult' ? t.routeConsultTitle : t.routeQueueTitle;
  const body = tri.route === 'queue' && !urgent ? t.routeQueueBody : t.routeConsultBody;
  return (
    <Card title={title} body={body} icon={tri.route === 'queue' && !urgent ? '✅' : '👩‍⚕️'}>
      <div className="stack">
        <button className={`btn btn-xl ${tri.route !== 'queue' || urgent ? 'btn-primary' : 'btn-secondary'}`} onClick={() => onChoose('video')}>🎥 {t.talkVideo}</button>
        <button className="btn btn-xl btn-secondary" onClick={() => onChoose('audio')}>📞 {t.talkAudio}</button>
        <button className={`btn btn-xl ${tri.route === 'queue' && !urgent ? 'btn-primary' : ''}`} onClick={() => onChoose('queue')}>🧾 {tri.route === 'queue' && !urgent ? t.printSummary : t.joinQueue}</button>
      </div>
      {!svc.online && <p className="notice">{t.consultOffline}</p>}
    </Card>
  );
}

// --- Print ---------------------------------------------------------------------------------

function receiptLines(t: Strings, s: Session, svc: KioskServices): string[] {
  const lines = describeVitals(s.vitals, svc.config.thresholds as ThresholdSet);
  const next = s.triage?.route === 'urgent_review' ? t.receiptNextUrgent : s.next === 'consult' || s.triage?.route === 'teleconsult' ? t.receiptNextConsult : t.receiptNextQueue;
  return [
    'MOVO-X · AJK',
    s.lang === 'ur' ? svc.config.facility.name_ur : svc.config.facility.name,
    new Date().toLocaleString('en-GB'),
    `${t.visitCode}: ${s.visitCode}`,
    '-'.repeat(32),
    ...lines.map((l) => (s.lang === 'ur' ? l.ur : l.en)),
    '-'.repeat(32),
    `${t.receiptNext}: ${next}`,
    t.receiptFooter,
  ];
}

function PrintStep({ t, s, svc, onDone }: { t: Strings; s: Session; svc: KioskServices; onDone: () => void }) {
  const [state, setState] = useState<'printing' | 'take'>('printing');
  useSpeak(t.takeReceipt, s.lang);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 400));
      try {
        await printReceipt(receiptLines(t, s, svc));
      } catch {
        /* printer fault is reported through the fleet heartbeat */
      }
      if (cancelled) return;
      setState('take');
      setTimeout(() => !cancelled && onDone(), 2500);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Card title={state === 'printing' ? t.printing : t.takeReceipt} icon="🧾">
      <div className="visit-code" dir="ltr">{s.visitCode}</div>
    </Card>
  );
}

function Receipt({ t, s, svc }: { t: Strings; s: Session; svc: KioskServices }) {
  if (!s.triage) return null;
  const lines = receiptLines(t, s, svc);
  return (
    <div id="receipt" className="receipt" dir={s.lang === 'ur' ? 'rtl' : 'ltr'} lang={s.lang} aria-hidden>
      {lines.map((l, i) => <div key={i} className={i === 3 ? 'r-code' : i === 0 ? 'r-head' : ''}>{l}</div>)}
      {svc.rx.length > 0 && (
        <>
          <div>{'-'.repeat(32)}</div>
          <div className="r-head">{t.prescription}</div>
          {svc.rx.map((r, i) => <div key={i}>{r.name ?? r.drugId} — {r.dose}, {r.frequency}, {r.days}d</div>)}
        </>
      )}
    </div>
  );
}

// --- Teleconsult (E09) ---------------------------------------------------------------------


function ConsultStep({ t, s, svc, onDone }: { t: Strings; s: Session; svc: KioskServices; onDone: () => void }) {
  const [phase, setPhase] = useState<'waiting' | 'live' | 'closed' | 'offline'>(svc.online ? 'waiting' : 'offline');
  const [mode, setMode] = useState<CallMode>(s.consultMode);
  const [clinician, setClinician] = useState<string | null>(null);
  const [messages, setMessages] = useState<{ sender: string; body: string }[]>([]);
  const [draft, setDraft] = useState('');
  const [result, setResult] = useState<{ summary: string | null; followUpDays: number | null; prescription: RxLine[] } | null>(null);
  const remoteRef = useRef<HTMLVideoElement>(null);
  const localRef = useRef<HTMLVideoElement>(null);
  const call = useRef<ConsultCall | null>(null);
  const joined = useRef(false);
  const consultId = s.consultId!;
  useSpeak(phase === 'waiting' ? t.consultWaiting : phase === 'offline' ? t.consultOffline : '', s.lang);

  const finish = useCallback(async () => {
    await svc.send('session.complete', { sessionId: s.sessionId, visitCode: s.visitCode, lastStep: 'consult', completedAt: new Date().toISOString() });
  }, [s.sessionId, s.visitCode, svc]);

  const startCall = useCallback((m: CallMode) => {
    if (!call.current) {
      const c = new ConsultCall(svc.rt, consultId, true);
      c.onRemote = (stream) => { if (remoteRef.current) remoteRef.current.srcObject = stream; };
      c.onLocal = (stream) => { if (localRef.current) localRef.current.srcObject = stream; };
      c.onState = (st) => { if (st === 'failed') setMode('chat'); };
      c.onModeRequest = (nm) => { setMode(nm); void c.start(nm); };
      call.current = c;
    }
    void call.current.start(m);
  }, [consultId, svc.rt]);

  const loadResult = useCallback(async () => {
    try {
      const r = await svc.api.get<{ status: string; clinician: string | null; summary: string | null; followUpDays: number | null; prescription: RxLine[] }>(`/api/kiosk/consults/${consultId}`);
      if (r.clinician) setClinician(r.clinician);
      if (r.status === 'active' && !joined.current) {
        joined.current = true;
        setPhase('live');
        svc.rt.join(consultId);
        startCall(mode);
      }
      if (r.status === 'closed') {
        call.current?.end();
        call.current = null;
        setResult(r);
        setPhase('closed');
        svc.setRx(r.prescription);
      }
    } catch {
      /* not yet synced; keep waiting */
    }
  }, [consultId, mode, phase, startCall, svc]);

  useEffect(() => {
    if (phase === 'offline') {
      void finish();
      const id = setTimeout(onDone, 9000);
      return () => clearTimeout(id);
    }
    const off = svc.rt.on((m: ServerMessage) => {
      if (m.type === 'event' && m.data.consultId === consultId && (m.event === 'consult.active' || m.event === 'consult.closed')) void loadResult();
      if (m.type === 'chat' && m.consultId === consultId) setMessages((x) => [...x, { sender: m.sender, body: m.body }]);
    });
    const poll = setInterval(loadResult, 5000);
    void loadResult();
    return () => {
      off();
      clearInterval(poll);
    };
  }, [consultId, finish, loadResult, onDone, phase, svc.rt]);

  useEffect(() => () => {
    call.current?.end();
    svc.rt.leave(consultId);
  }, [consultId, svc.rt]);

  const switchMode = (m: CallMode) => {
    setMode(m);
    call.current?.requestMode(m);
    void call.current?.start(m);
  };

  if (phase === 'offline') return <Card title={t.consultWaiting} body={t.consultOffline} icon="📨" />;
  if (phase === 'waiting') {
    return (
      <Card title={t.consultWaiting} body={t.consultWaitingBody} icon="👩‍⚕️">
        <div className="spin big" />
      </Card>
    );
  }
  if (phase === 'closed' && result) {
    return (
      <Card title={t.consultDone} icon="✅">
        {result.summary && <p className="lead" dir="auto" lang={/[؀-ۿ]/.test(result.summary) ? 'ur' : 'en'}>{result.summary}</p>}
        {result.prescription.length > 0 && (
          <div className="rx">
            <h3>{t.prescription}</h3>
            <ul lang="en" dir="ltr">{result.prescription.map((r, i) => <li key={i}>{r.name ?? r.drugId} — {r.dose}, {r.frequency}, {r.days} d{r.instructions ? ` (${r.instructions})` : ''}</li>)}</ul>
          </div>
        )}
        {result.followUpDays ? <p>{t.followUp}: {result.followUpDays} {t.days}</p> : null}
        <div className="k-footer">
          {result.prescription.length > 0 && <button className="btn btn-xl" onClick={() => printReceipt([t.prescription, ...result.prescription.map((r) => `${r.name ?? r.drugId} ${r.dose} ${r.frequency} ${r.days}d`)])}>🧾 {t.printPrescription}</button>}
          <button className="btn btn-primary btn-xl" onClick={async () => { await finish(); onDone(); }}>{t.next}</button>
        </div>
      </Card>
    );
  }
  return (
    <section className="k-card consult-live">
      <div className="consult-head">
        <strong>{t.consultConnected}</strong>
        {clinician && <span className="muted"> · {clinician}</span>}
      </div>
      {mode !== 'chat' && (
        <div className="video-wrap">
          <video ref={remoteRef} autoPlay playsInline className="video-remote" />
          <video ref={localRef} autoPlay playsInline muted className="video-local" />
        </div>
      )}
      <div className="chat">
        <div className="chat-log">
          {messages.map((m, i) => <div key={i} className={`msg ${m.sender === 'patient' ? 'me' : ''}`}>{m.body}</div>)}
        </div>
        <div className="row">
          <input className="input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t.typeHere} />
          <button className="btn btn-primary" onClick={() => { if (draft.trim()) { svc.rt.send({ type: 'chat', consultId, body: draft }); setDraft(''); } }}>{t.send}</button>
        </div>
      </div>
      <div className="row">
        {mode !== 'video' && <button className="btn" onClick={() => switchMode('video')}>🎥 {t.switchVideo}</button>}
        {mode !== 'audio' && <button className="btn" onClick={() => switchMode('audio')}>📞 {t.switchAudio}</button>}
        {mode !== 'chat' && <button className="btn" onClick={() => switchMode('chat')}>💬 {t.switchChat}</button>}
      </div>
    </section>
  );
}

// --- Done & reset --------------------------------------------------------------------------

function Done({ t, s, svc, onReset }: { t: Strings; s: Session; svc: KioskServices; onReset: () => void }) {
  const [left, setLeft] = useState(12);
  useSpeak(`${t.doneTitle} ${t.doneBody}`, s.lang);
  useEffect(() => {
    if (s.next !== 'consult') void svc.send('session.complete', { sessionId: s.sessionId, visitCode: s.visitCode, lastStep: 'reset', completedAt: new Date().toISOString() });
    const iv = setInterval(() => setLeft((x) => x - 1), 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (left <= 0) {
      svc.setRx([]);
      onReset();
    }
  }, [left, onReset, svc]);
  return (
    <Card title={t.doneTitle} body={t.doneBody} icon="🙏">
      <div className="visit-code" dir="ltr">{s.visitCode}</div>
      <p className="muted">{left} {t.resetIn}</p>
      <button className="btn btn-primary btn-xl" onClick={() => { svc.setRx([]); onReset(); }}>✓</button>
    </Card>
  );
}
